import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { BriefcaseBusiness, Search } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function AuthCallback() {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const processed = useRef(false);
  const [error, setError] = useState("");
  const [newUser, setNewUser] = useState(null);
  const [roleBusy, setRoleBusy] = useState("");

  const destinationFor = (u) => (u.role === "admin" ? "/control-tower" : "/dashboard");

  useEffect(() => {
    if (processed.current) return;  // StrictMode double-run guard: exchange once
    processed.current = true;

    const finish = async (request) => {
      try {
        const r = await request;
        if (r.data.token) localStorage.setItem("ht_token", r.data.token);
        window.history.replaceState(null, "", window.location.pathname);  // code/session_id is single-use
        await refreshUser().catch(() => {});
        if (r.data.new_account) {
          setNewUser(r.data);  // genuinely new Google account → role selection
          return;
        }
        navigate(destinationFor(r.data), { replace: true });
      } catch (err) {
        setError(err?.response?.data?.detail || "Google sign-in could not be completed. Please try again.");
      }
    };

    const params = new URLSearchParams(window.location.search);
    const oauthError = params.get("error");
    if (oauthError) {
      setError(oauthError === "access_denied"
        ? "You cancelled the Google sign-in. Nothing was changed."
        : `Google returned an error: ${oauthError}`);
      return;
    }
    // Dedicated HiredToday Google client returns ?code= in the query string
    const code = params.get("code");
    if (code) {
      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
      finish(api.post("/auth/google/code", { code, redirect_uri: window.location.origin + "/auth/callback" }));
      return;
    }
    setError("Missing sign-in details from Google. Please start again from the login page.");
  }, [navigate, refreshUser]);

  const chooseRole = async (role) => {
    setRoleBusy(role);
    try {
      const r = await api.post("/auth/google/role", { role });
      await refreshUser().catch(() => {});
      navigate(destinationFor(r.data), { replace: true });
    } catch {
      setError("Could not save your choice. Please try again.");
    } finally {
      setRoleBusy("");
    }
  };

  return (
    <main className="min-h-[60vh] flex items-center justify-center px-4" data-testid="auth-callback">
      {error ? (
        <div className="max-w-md w-full border border-red-200 bg-red-50/50 rounded-md p-8 text-center" data-testid="auth-callback-error">
          <p className="font-display text-xl font-bold text-zinc-950">We couldn't sign you in</p>
          <p className="text-sm text-zinc-600 mt-3 leading-relaxed" data-testid="auth-callback-error-detail">{error}</p>
          <div className="mt-6 flex flex-col sm:flex-row gap-2 justify-center">
            <Link to="/login" data-testid="auth-callback-retry"
              className="h-11 px-6 bg-zinc-950 text-white text-sm font-semibold rounded-md hover:bg-zinc-800 transition-colors duration-200 inline-flex items-center justify-center">
              Back to sign in
            </Link>
            <Link to="/register" data-testid="auth-callback-register"
              className="h-11 px-6 border border-zinc-300 text-sm font-semibold rounded-md hover:border-zinc-500 transition-colors duration-200 inline-flex items-center justify-center">
              Create an account
            </Link>
          </div>
        </div>
      ) : newUser ? (
        <div className="max-w-md w-full border border-zinc-200 bg-white rounded-md p-8 text-center" data-testid="auth-callback-role">
          <p className="font-display text-xl font-bold text-zinc-950">Welcome{newUser.name ? `, ${newUser.name.split(" ")[0]}` : ""}!</p>
          <p className="text-sm text-zinc-600 mt-2">One last thing: what brings you to HiredToday?</p>
          <div className="mt-6 grid gap-3">
            <button onClick={() => chooseRole("candidate")} disabled={!!roleBusy} data-testid="auth-role-candidate"
              className="h-14 px-5 bg-zinc-950 text-white text-sm font-semibold rounded-md hover:bg-zinc-800 disabled:opacity-50 transition-colors duration-200 inline-flex items-center justify-center gap-2">
              <Search className="h-4 w-4" /> {roleBusy === "candidate" ? "Setting up…" : "I'm looking for work"}
            </button>
            <button onClick={() => chooseRole("employer")} disabled={!!roleBusy} data-testid="auth-role-employer"
              className="h-14 px-5 border border-zinc-300 text-sm font-semibold rounded-md hover:border-zinc-500 disabled:opacity-50 transition-colors duration-200 inline-flex items-center justify-center gap-2">
              <BriefcaseBusiness className="h-4 w-4" /> {roleBusy === "employer" ? "Setting up…" : "I'm hiring staff"}
            </button>
          </div>
        </div>
      ) : (
        <div className="text-center">
          <div className="h-8 w-8 border-2 border-zinc-300 border-t-zinc-900 rounded-full animate-spin mx-auto" />
          <p className="text-sm text-zinc-500 mt-4">Signing you in with Google…</p>
        </div>
      )}
    </main>
  );
}
